import { useState, useEffect, useCallback } from 'react';
import { isAutoFitDebugActive, addDebugLog } from '../../ui/components/debug/DebugUtils';

interface UseAutoTextFitOptions {
    minFontSize?: number;
    maxFontSize?: number;
    lineHeight?: number;
    padding?: number;
}

interface UseAutoTextFitResult {
    fontSize: number;
    isAdjusting: boolean;
    ref: React.RefObject<HTMLDivElement>;
}

/**
 * Hook para ajustar dinámicamente el tamaño de fuente de un contenedor
 * para que el contenido quepa sin hacer scroll.
 */
export const useAutoTextFit = (
    content: React.ReactNode,
    {
        minFontSize = 12,
        maxFontSize = 100,
        lineHeight = 1.4,
        padding = 0,
    }: UseAutoTextFitOptions = {}
): UseAutoTextFitResult => {
    const [fontSize, setFontSize] = useState(maxFontSize);
    const [isAdjusting, setIsAdjusting] = useState(true);
    const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

    // Ref callback para manejar la inicialización del nodo
    const ref = useCallback((node: HTMLDivElement) => {
        setContainerRef(node);
    }, []);

    useEffect(() => {
        if (!containerRef) return;

        const adjustFontSize = () => {
            const startTime = performance.now();
            setIsAdjusting(true);

            const container = containerRef;
            if (!container) return;

            // Encontrar el contenedor interno con el contenido real
            const contentWrapper = container.querySelector('#auto-fit-content-wrapper') as HTMLElement;
            if (!contentWrapper) {
                setIsAdjusting(false);
                return;
            }

            // Resetear estilos para medición limpia
            container.style.fontSize = `${maxFontSize}px`;
            container.style.lineHeight = `${lineHeight}`;

            // Altura disponible real - restar un margen de seguridad de 20px
            const containerRect = container.getBoundingClientRect();
            const availableHeight = containerRect.height - 20;


            // Logging inicial
            if (isAutoFitDebugActive()) {
                addDebugLog('useAutoTextFit', 'Iniciando ajuste de fuente', {
                    minFontSize,
                    maxFontSize,
                    availableHeight,
                    lineHeight,
                    padding,
                });
            }

            // Si no hay altura disponible (ej: oculto), salir
            if (availableHeight <= 0) {
                if (isAutoFitDebugActive()) {
                    addDebugLog('useAutoTextFit', 'Sin altura disponible, abortando', {
                        availableHeight,
                    });
                }
                setIsAdjusting(false);
                return;
            }

            // Función helper para verificar overflow
            const checkOverflow = (): boolean => {
                // Forzar reflow
                void container.offsetHeight;
                void contentWrapper.offsetHeight;

                // Recalcular la altura del contenedor en cada verificación
                const currentContainerRect = container.getBoundingClientRect();
                const containerHeight = currentContainerRect.height;

                // Medir el contenido real (el wrapper interno)
                const contentHeight = contentWrapper.scrollHeight;

                // Verificar si el contenido excede el contenedor
                return contentHeight > (containerHeight + 1); // Tolerancia de 1px
            };

            // Algoritmo de búsqueda binaria mejorado para encontrar el tamaño MÁXIMO que quepa
            let low = minFontSize;
            let high = maxFontSize;
            let optimalSize = minFontSize;

            // Iteraciones limitadas para evitar loops infinitos
            let iterations = 0;
            const maxIterations = 60; // Aumentado para mayor precisión con mucho contenido
            const iterationLogs: Array<{ mid: number; hasOverflow: boolean; optimalSize: number }> = [];

            // Primera pasada: búsqueda binaria estándar
            while (low <= high && iterations < maxIterations) {
                const mid = Math.floor((low + high) / 2);
                container.style.fontSize = `${mid}px`;
                void container.offsetHeight;
                void contentWrapper.offsetHeight;

                const hasOverflow = checkOverflow();

                if (hasOverflow) {
                    // Si hay overflow, necesitamos reducir la fuente
                    high = mid - 1;
                } else {
                    // Si cabe completamente, guardamos este tamaño como posible candidato y probamos uno mayor
                    optimalSize = mid;
                    low = mid + 1;
                }
                iterations++;

                if (isAutoFitDebugActive() && iterations % 10 === 0) {
                    iterationLogs.push({ mid, hasOverflow, optimalSize });
                }
            }

            if (isAutoFitDebugActive() && iterationLogs.length > 0) {
                addDebugLog('useAutoTextFit', `Búsqueda binaria completada (${iterations} iteraciones)`, {
                    iterations,
                    finalOptimalSize: optimalSize,
                    sampleIterations: iterationLogs,
                });
            }

            // Verificación final: asegurar que el tamaño óptimo realmente cabe
            // Si el tamaño óptimo es minFontSize y aún hay overflow, forzar que quepa
            container.style.fontSize = `${optimalSize}px`;

            // Verificar si aún hay overflow
            if (checkOverflow() && optimalSize > minFontSize) {
                // Reducir hasta que quepa completamente
                let safeSize = optimalSize;
                while (safeSize > minFontSize && checkOverflow()) {
                    safeSize -= 1;
                    container.style.fontSize = `${safeSize}px`;
                }
                optimalSize = safeSize;
            } else if (!checkOverflow() && optimalSize < maxFontSize) {
                // Si hay espacio extra, intentar aumentar más agresivamente (hasta 50px más)
                let extraSize = optimalSize + 1;
                while (extraSize <= optimalSize + 50 && extraSize <= maxFontSize) {
                    container.style.fontSize = `${extraSize}px`;
                    if (checkOverflow()) {
                        break; // Si desborda, el tamaño anterior era el máximo
                    }
                    optimalSize = extraSize;
                    extraSize++;
                }
            }

            // Verificación final absoluta: asegurar que el contenido quepa
            container.style.fontSize = `${optimalSize}px`;
            void container.offsetHeight;
            void contentWrapper.offsetHeight;

            // Si después de todo aún hay overflow, reducir más agresivamente
            if (checkOverflow() && optimalSize > minFontSize) {
                let finalSize = optimalSize;
                // Reducir más agresivamente cuando hay overflow
                while (finalSize > minFontSize && checkOverflow()) {
                    finalSize -= 2; // Reducir de 2 en 2 para ser más agresivo
                    container.style.fontSize = `${finalSize}px`;
                    void container.offsetHeight;
                    void contentWrapper.offsetHeight;
                }
                optimalSize = finalSize;

                // Si aún hay overflow después de reducir agresivamente, hacer una pasada final más precisa
                if (checkOverflow() && optimalSize > minFontSize) {
                    while (optimalSize > minFontSize && checkOverflow()) {
                        optimalSize -= 1;
                        container.style.fontSize = `${optimalSize}px`;
                        void container.offsetHeight;
                        void contentWrapper.offsetHeight;
                    }
                }

                // Si llegamos a minFontSize y aún hay overflow, usar minFontSize de todas formas
                if (checkOverflow() && optimalSize <= minFontSize) {
                    optimalSize = minFontSize;
                    container.style.fontSize = `${optimalSize}px`;
                }
            }

            // Aplicar tamaño final
            const finalContainerRect = container.getBoundingClientRect();
            const finalContentHeight = contentWrapper.scrollHeight;
            const finalContainerHeight = finalContainerRect.height;
            const finalHasOverflow = checkOverflow();
            const executionTime = performance.now() - startTime;

            // Logging final
            if (isAutoFitDebugActive()) {
                addDebugLog('useAutoTextFit', 'Ajuste de fuente completado', {
                    optimalSize,
                    finalContentHeight,
                    finalContainerHeight,
                    finalHasOverflow,
                    executionTime: `${executionTime.toFixed(2)}ms`,
                    iterations,
                    minFontSize,
                    maxFontSize,
                });
            }

            setFontSize(optimalSize);
            container.style.fontSize = `${optimalSize}px`;
            setIsAdjusting(false);
        };

        // Observer para cambios de tamaño en el contenedor
        const resizeObserver = new ResizeObserver(() => {
            // Usar requestAnimationFrame para evitar errores de "ResizeObserver loop limit exceeded"
            requestAnimationFrame(adjustFontSize);
        });

        resizeObserver.observe(containerRef);

        // Ejecutar ajuste inicial
        adjustFontSize();

        return () => {
            resizeObserver.disconnect();
        };
    }, [containerRef, content, minFontSize, maxFontSize, lineHeight, padding]);

    return { fontSize, isAdjusting, ref: ref as any };
};
